import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from '~/components/EmptyState';
import { fetchCalendarEvents } from './api';
import { CalendarGrid } from './components/CalendarGrid';
import { CalendarHeader } from './components/CalendarHeader';
import { EventDetailPanel } from './components/EventDetailPanel';
import { LayerFilterBar } from './components/LayerFilterBar';
import { CalendarEvent, CalendarFilters, CalendarLayerState, CalendarViewMode } from './types';
import {
  buildMonthGrid,
  buildWeekDays,
  filterEvents,
  groupEventsByDate,
  toISODate,
  uniqueFacilities,
} from './utils';

const DEFAULT_LAYERS: CalendarLayerState = {
  contracts: true,
  listings: true,
  b2b: true,
  billing: true,
  drafts: false,
};

const DEFAULT_FILTERS: CalendarFilters = {
  facility: 'all',
  contractType: 'all',
  b2bRole: 'all',
  search: '',
};

export const EmployerCalendarPage = () => {
  const [anchor, setAnchor] = useState(() => new Date());
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [layers, setLayers] = useState(DEFAULT_LAYERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Fetch exactly the visible grid range. The API's `end` is inclusive, so we
  // pass the last visible day directly — adding a day would pull events from an
  // off-grid day into derived state (facility filter, counts) without rendering.
  const range = useMemo(() => {
    const days = viewMode === 'month' ? buildMonthGrid(anchor) : buildWeekDays(anchor);
    return { start: toISODate(days[0]), end: toISODate(days[days.length - 1]) };
  }, [anchor, viewMode]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchCalendarEvents(range.start, range.end, layers.drafts)
      .then((data) => {
        if (!cancelled) setEvents(data);
      })
      .catch((err) => {
        console.debug('EmployerCalendarPage: failed to load events', err);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range.start, range.end, layers.drafts]);

  const facilities = useMemo(() => uniqueFacilities(events), [events]);
  const eventsByDate = useMemo(
    () => groupEventsByDate(filterEvents(events, layers, filters)),
    [events, layers, filters]
  );

  return (
    <div className="flex flex-col gap-4" data-cy="employer-calendar-page">
      <div className="flex items-center justify-between">
        <h1 className="sub-heading-sb text-grey-100">Calendar</h1>
        {loading && <span className="body-text text-grey-500">Loading events…</span>}
      </div>

      <CalendarHeader
        anchor={anchor}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onAnchorChange={setAnchor}
      />

      <LayerFilterBar
        layers={layers}
        onLayersChange={setLayers}
        filters={filters}
        onFiltersChange={setFilters}
        facilities={facilities}
      />

      {error ? (
        <div className="rounded-xl border border-grey-900 bg-white shadow-sm">
          <EmptyState
            icon={<CalendarDaysIcon className="size-10 text-grey-700" />}
            title="Unable to load calendar events"
            description="Please try again."
            cyTag="employer-calendar-error"
          />
        </div>
      ) : (
        <CalendarGrid
          anchor={anchor}
          viewMode={viewMode}
          eventsByDate={eventsByDate}
          onSelectEvent={setSelectedEvent}
        />
      )}

      <EventDetailPanel event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
};
